import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { buildMoreDanceUrl, formatMoreDanceDate, moreDanceButtonState,
  moreDanceErrorMessage, parseMoreDanceList, parseMoreDanceReservation } from "./moreDanceState.mjs";
import "./MoreDance.scss";

const session = () => ({
  cookie: localStorage.getItem("session_user_login_cookie"),
  userId: localStorage.getItem("session_user_id"),
  userType: localStorage.getItem("session_user_type"),
  schoolId: localStorage.getItem("session_school_id"),
});

const apiUrl = (action) => buildMoreDanceUrl(process.env.REACT_APP_API_BASE_URL, session(), action);

const MoreDance = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState("");
  const [refreshRequired, setRefreshRequired] = useState(false);
  const mounted = useRef(true);
  const requestNumber = useRef(0);
  const reservationInFlight = useRef(false);
  const uncertainReservation = useRef(null);

  const refreshCourses = useCallback(async () => {
    const request = ++requestNumber.current;
    if (mounted.current) { setLoading(true); setLoadError(""); }
    try {
      const response = await axios.get(apiUrl("get_more_dance_courses"), { timeout: 20000 });
      const list = parseMoreDanceList(response.data);
      if (!mounted.current || request !== requestNumber.current) return null;
      setCourses(list.courses);
      setEmptyMessage(list.message);
      setRefreshRequired(false);
      if (uncertainReservation.current) {
        const previous = uncertainReservation.current;
        if (list.courses.some((item) => item.id === previous.id && item.reserved)) {
          setFeedback({ kind: "success", text: `${previous.title}: Dein zusätzlicher fester Platz ist reserviert. Dein Hauptkurs bleibt unverändert.` });
        }
        uncertainReservation.current = null;
      }
      return list.courses;
    } catch (error) {
      if (mounted.current && request === requestNumber.current) {
        setRefreshRequired(true);
        setLoadError(error.response ? moreDanceErrorMessage(error.response.data) :
          (error.message && !error.isAxiosError ? error.message : "Die Kursliste ist momentan nicht erreichbar. Bitte versuche es erneut."));
      }
      return null;
    } finally {
      if (mounted.current && request === requestNumber.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refreshCourses();
    return () => { mounted.current = false; requestNumber.current += 1; };
  }, [refreshCourses]);

  const reserveCourse = async (course) => {
    // The ref prevents two POSTs before React has rendered the disabled buttons.
    if (reservationInFlight.current || course.reserved || loading || refreshRequired) return;
    reservationInFlight.current = true;
    setPendingId(course.id);
    setFeedback(null);
    try {
      const form = new FormData();
      form.append("user_id", session().userId);
      form.append("course_id", String(course.id));
      form.append("confirmed", "1");
      const response = await axios.post(apiUrl("reserve_more_dance_course"), form, { timeout: 20000 });
      const receipt = parseMoreDanceReservation(response.data, course.id);
      if (!mounted.current) return;
      setCourses((current) => current.map((item) => item.id === course.id ?
        { ...item, reserved: true, startDate: receipt.startDate || item.startDate } : item));
      setFeedback({ kind: "success", text: `${course.title}: Dein zusätzlicher fester Platz ${receipt.alreadyReserved ? "ist bereits" : "ist"} reserviert.${receipt.startDate ? ` Erster Termin: ${formatMoreDanceDate(receipt.startDate)}.` : ""} Dein Hauptkurs bleibt unverändert.` });
      await refreshCourses();
    } catch (error) {
      if (!mounted.current) return;
      const reason = error.response ? moreDanceErrorMessage(error.response.data) :
        (error.message && !error.isAxiosError ? error.message : "Die Reservierung konnte noch nicht bestätigt werden.");
      // A timed-out POST may have succeeded. Read current server state before offering a retry.
      setRefreshRequired(true);
      uncertainReservation.current = course;
      const latest = await refreshCourses();
      if (!mounted.current) return;
      if (latest?.some((item) => item.id === course.id && item.reserved)) {
        setFeedback({ kind: "success", text: `${course.title}: Dein zusätzlicher fester Platz ist reserviert. Dein Hauptkurs bleibt unverändert.` });
      } else {
        setFeedback({ kind: "error", text: `${reason}${latest ? " Die Kursliste wurde aktualisiert." : " Bitte aktualisiere die Kursliste, bevor du es erneut versuchst."}` });
      }
    } finally {
      reservationInFlight.current = false;
      if (mounted.current) setPendingId(null);
    }
  };

  return (
    <section className="more_dance_section">
      <div className="container-fluid">
        <div className="more_dance_heading"><h2>Mehr Tanzen?</h2></div>
        <div className="more_dance_intro">
          <h3>Dein zusätzlicher fester Kursplatz</h3>
          <p>Hier findest du Kurse, in denen wir noch passende Tanzpartnerinnen oder Tanzpartner brauchen. Du siehst freie Plätze für dein Profil, in deiner Tanzrichtung und auf deinem Level oder tiefer.</p>
          <p><strong>Mit „Fixen Platz reservieren“ meldest du dich verbindlich für diesen zusätzlichen wöchentlichen Kurs an. Dein Hauptkurs bleibt unverändert.</strong></p>
          <p>Wenn du einmal nicht teilnehmen kannst, trage bitte eine Absenz ein.</p>
        </div>
        {feedback && <div className={`more_dance_feedback ${feedback.kind}`} role={feedback.kind === "error" ? "alert" : "status"}>{feedback.text}</div>}
        <div className="more_dance_toolbar">
          <p>Freie Plätze werden bei der Reservierung nochmals geprüft.</p>
          <button type="button" className="btn btn_secondary" onClick={refreshCourses} disabled={loading || Boolean(pendingId)}>Aktualisieren</button>
        </div>
        {loading && <p role="status" className="more_dance_state">Passende Kurse werden geladen …</p>}
        {loadError && <div className="more_dance_feedback error" role="alert"><p>{loadError}</p><button type="button" className="btn btn_secondary" onClick={refreshCourses} disabled={loading || Boolean(pendingId)}>Erneut laden</button></div>}
        {!loading && !loadError && courses.length === 0 && <div className="more_dance_state"><h3>Aktuell kein passender freier Platz</h3><p>{emptyMessage || "Für dein Profil ist momentan kein zusätzlicher Kurs mit freiem Platz verfügbar. Schau später nochmals vorbei."}</p></div>}
        {!loadError && courses.length > 0 && <div className="row more_dance_courses" aria-busy={loading || Boolean(pendingId)}>
          {courses.map((course) => {
            const button = moreDanceButtonState(course, pendingId, loading, refreshRequired);
            const roleLabel = course.role === "female" ? "Frauen" : "Männer";
            return <div className="col-xl-6 col-12" key={course.id}>
              <article className="more_dance_card" aria-labelledby={`more-dance-course-${course.id}`}>
                <div className="more_dance_card_heading"><span>{course.style} · {course.levelLabel}</span><h3 id={`more-dance-course-${course.id}`}>{course.title}</h3></div>
                <dl><div><dt>Wöchentlich</dt><dd>{course.weekday}, {course.time}{course.endTime ? `–${course.endTime}` : ""} Uhr</dd></div>
                  {course.startDate && <div><dt>Erster Termin</dt><dd>{formatMoreDanceDate(course.startDate)}</dd></div>}
                  <div><dt>Dein Platz</dt><dd>{course.reserved ? "Fest reserviert" : `${course.availablePlaces} ${course.availablePlaces === 1 ? "freier Platz" : "freie Plätze"} für ${roleLabel}`}</dd></div>
                </dl>
                <button type="button" className={`btn ${course.reserved ? "btn_secondary" : "btn_primary"}`} onClick={() => reserveCourse(course)} disabled={button.disabled} aria-label={`${button.text}: ${course.title}`}>{button.text}</button>
              </article>
            </div>;
          })}
        </div>}
      </div>
    </section>
  );
};

export default MoreDance;
